import { useMutation } from '@apollo/client';
import type { InsertChildImpactRatingsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertChildImpactRatingsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertChildImpactRatingsTRPCInput } from './useInsertChildImpactRatingsTRPC';
import { useInsertChildImpactRatingsTRPC } from './useInsertChildImpactRatingsTRPC';

export const useInsertChildImpactRatings = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertImpactRatingsGraphQL, graphqlState] = useMutation(
    InsertChildImpactRatingsDocument,
    {
      update: (cache) => {
        evictField(cache, 'impact_rating');
        evictField(cache, 'assessment');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertChildImpactRatingsTRPC();

  const insertChildImpactRatings = async (
    variables: InsertChildImpactRatingsTRPCInput
  ): Promise<InsertChildImpactRatingsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertChildImpactRatings(variables);
    }

    const result = await insertImpactRatingsGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert impact ratings');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertChildImpactRatings,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertChildImpactRatings,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
